import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "@/components/ui/sonner";

const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setSending(true);

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact-2bwbat", ...form }),
    })
      .then(() => {
        setSent(true);
        toast.success("Message envoyé ! Nous vous recontacterons vite.");
        setForm({ name: "", email: "", phone: "", message: "" });
      })
      .catch(() => toast.error("Erreur d'envoi. Veuillez réessayer."))
      .finally(() => setSending(false));
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <Toaster position="top-right" />

      <section className="py-12 md:py-20 bg-[#E5E0D8]/30 min-h-[calc(100vh-80px)] flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            
            <motion.div initial="hidden" animate="visible" variants={stagger} className="pt-4 lg:pt-8">
              <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Contact</motion.p>
              <motion.h1 variants={fadeIn} className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight text-[#2C2C2C] mt-4">
                Parlons de<br />votre projet
              </motion.h1>
              <motion.div variants={fadeIn} className="section-divider mt-8 mb-8" />
              <motion.p variants={fadeIn} className="text-lg font-light leading-relaxed text-[#2C2C2C]/70 max-w-lg mb-12">
                Chaque projet commence par une conversation. Contactez-nous pour un diagnostic gratuit et un devis personnalisé.
              </motion.p>

              <div className="space-y-6">
                <motion.div variants={fadeIn} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C5A065]/30 bg-white shadow-sm">
                    <MapPin className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2C] text-sm">Adresse</p>
                    <p className="text-sm text-[#8A8A8A] mt-1">Paris 14e arrondissement<br />Ile-de-France</p>
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C5A065]/30 bg-white shadow-sm">
                    <Phone className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2C] text-sm">Téléphone</p>
                    <a href="tel:+33666777517" className="text-sm text-[#8A8A8A] hover:text-[#C5A065] transition-colors mt-1 block">06 66 77 75 17</a>
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C5A065]/30 bg-white shadow-sm">
                    <Mail className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2C] text-sm">Email</p>
                    <a href="mailto:2bwbat@gmail.com" className="text-sm text-[#8A8A8A] hover:text-[#C5A065] transition-colors mt-1 block">2bwbat@gmail.com</a>
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C5A065]/30 bg-white shadow-sm">
                    <Clock className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2C] text-sm">Disponibilité</p>
                    <p className="text-sm text-[#8A8A8A] mt-1">Disponible 7j/7 pour<br />vos urgences et projets.</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={stagger} className="bg-white p-8 md:p-10 lg:p-12 border border-border/50 shadow-sm mt-8 lg:mt-0">
              <motion.h2 variants={fadeIn} className="font-heading text-2xl font-medium text-[#2C2C2C] mb-8">Demande de devis</motion.h2>

              {sent ? (
                <motion.div variants={fadeIn} className="bg-[#E5E0D8]/20 border border-[#C5A065]/30 p-8 md:p-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-[#C5A065] mx-auto mb-6" />
                  <h3 className="font-heading text-2xl text-[#2C2C2C] mb-3">Message envoyé</h3>
                  <p className="text-sm text-[#8A8A8A]">Merci pour votre demande. Notre équipe vous recontactera dans les plus brefs délais.</p>
                  <Button variant="outline" className="border-[#C5A065] text-[#C5A065] hover:bg-[#C5A065] hover:text-white rounded-none mt-8 px-6 py-3 text-sm tracking-widest uppercase" onClick={() => setSent(false)}>Envoyer un autre message</Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} name="contact-2bwbat" data-netlify="true" className="space-y-6">
                  <input type="hidden" name="form-name" value="contact-2bwbat" />
                  <motion.div variants={fadeIn}>
                    <Label htmlFor="name" className="text-sm font-medium text-[#2C2C2C] mb-1 block">Nom complet <span className="text-[#C5A065]">*</span></Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} required className="bg-transparent border-0 border-b border-[#8A8A8A]/30 focus:border-[#C5A065] rounded-none px-0 py-3 placeholder:text-[#8A8A8A]/50 focus-visible:ring-0 focus-visible:ring-offset-0" />
                  </motion.div>
                  <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-[#2C2C2C] mb-1 block">Email <span className="text-[#C5A065]">*</span></Label>
                      <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required className="bg-transparent border-0 border-b border-[#8A8A8A]/30 focus:border-[#C5A065] rounded-none px-0 py-3 placeholder:text-[#8A8A8A]/50 focus-visible:ring-0 focus-visible:ring-offset-0" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-[#2C2C2C] mb-1 block">Téléphone</Label>
                      <Input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} className="bg-transparent border-0 border-b border-[#8A8A8A]/30 focus:border-[#C5A065] rounded-none px-0 py-3 placeholder:text-[#8A8A8A]/50 focus-visible:ring-0 focus-visible:ring-offset-0" />
                    </div>
                  </motion.div>
                  <motion.div variants={fadeIn}>
                    <Label htmlFor="message" className="text-sm font-medium text-[#2C2C2C] mb-1 block">Votre projet <span className="text-[#C5A065]">*</span></Label>
                    <Textarea id="message" name="message" value={form.message} onChange={handleChange} required rows={4} className="bg-transparent border-0 border-b border-[#8A8A8A]/30 focus:border-[#C5A065] rounded-none px-0 py-3 placeholder:text-[#8A8A8A]/50 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none" />
                  </motion.div>
                  <motion.div variants={fadeIn} className="pt-4">
                    <Button type="submit" disabled={sending} className="group bg-[#C5A065] text-white hover:bg-[#C5A065]/90 px-10 py-6 text-sm tracking-widest uppercase rounded-none w-full">
                      {sending ? "Envoi en cours..." : "Envoyer ma demande"}
                      <Send className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </Button>
                  </motion.div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
